class CollectionInfiniteScroll extends HTMLElement {
  constructor() {
    super();
    this.currentPage = 1;
    this.isLoading = false;
    this.hasMorePages = true;
    this.threshold = 800; // pixels from bottom to trigger load
    this.highestPageLoaded = 1; // Track the highest page loaded
    this.showPagesInUrl = this.dataset.showPagesInUrl === 'true'; // Get setting from data attribute
    
    this.productList = this.querySelector('.product-list');
    this.paginationElement = this.querySelector('.pagination');
    
    if (this.paginationElement) {
      this.setupInfiniteScroll();
    }
  }

  setupInfiniteScroll() {
    // Hide the original pagination
    if (this.paginationElement) {
      this.paginationElement.style.display = 'none';
    }

    // Create and append loading indicator
    this.loadingIndicator = this.createLoadingIndicator();
    this.appendChild(this.loadingIndicator);

    // Extract total pages from pagination
    this.extractPaginationInfo();

    // Bind scroll event
    this.boundHandleScroll = this.handleScroll.bind(this);
    window.addEventListener('scroll', this.boundHandleScroll);
    
    // Check if we need to load immediately (in case content is shorter than viewport)
    this.checkIfNeedsLoad();
  }

  extractPaginationInfo() {
    // Try to find the next page link
    const nextLink = this.paginationElement?.querySelector('a[rel="next"]');
    if (!nextLink) {
      this.hasMorePages = false;
      return;
    }

    // Extract current page from URL or assume page 1
    const urlParams = new URLSearchParams(window.location.search);
    this.currentPage = parseInt(urlParams.get('page')) || 1;

    // Check if there are more pages
    this.hasMorePages = !!nextLink;
  }

  createLoadingIndicator() {
    const indicator = document.createElement('div');
    indicator.className = 'infinite-scroll-loading';
    indicator.style.cssText = `
      display: none;
      text-align: center;
      padding: 40px 20px;
      color: #999;
    `;
    indicator.innerHTML = `
      <div class="spinner" style="
        border: 3px solid #f3f3f3;
        border-top: 3px solid #333;
        border-radius: 50%;
        width: 40px;
        height: 40px;
        animation: spin 1s linear infinite;
        margin: 0 auto;
      "></div>
      <style>
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      </style>
    `;
    return indicator;
  }

  handleScroll() {
    // Check if we should reset URL to page 1 when scrolling to top
    this.checkScrollPosition();

    if (this.isLoading || !this.hasMorePages) {
      return;
    }

    const scrollPosition = window.innerHeight + window.scrollY;
    const pageHeight = document.documentElement.scrollHeight;

    if (pageHeight - scrollPosition < this.threshold) {
      this.loadNextPage();
    }
  }

  checkScrollPosition() {
    // Only update URL if the setting is enabled
    if (!this.showPagesInUrl) {
      return;
    }

    // If user scrolls near the top and we're on a page > 1, reset URL to page 1
    const scrollY = window.scrollY;
    const threshold = 200; // pixels from top
    
    if (scrollY < threshold && this.highestPageLoaded > 1) {
      const url = new URL(window.location.href);
      const currentUrlPage = url.searchParams.get('page');
      
      // Only update if URL currently has a page parameter
      if (currentUrlPage && currentUrlPage !== '1') {
        url.searchParams.delete('page');
        window.history.replaceState({}, '', url);
      }
    }
  }

  checkIfNeedsLoad() {
    // If the page content is shorter than viewport, load more
    setTimeout(() => {
      const pageHeight = document.documentElement.scrollHeight;
      const viewportHeight = window.innerHeight;
      
      if (pageHeight <= viewportHeight && this.hasMorePages && !this.isLoading) {
        this.loadNextPage();
      }
    }, 100);
  }

  async loadNextPage() {
    if (this.isLoading || !this.hasMorePages) {
      return;
    }

    this.isLoading = true;
    this.loadingIndicator.style.display = 'block';

    const nextPage = this.currentPage + 1;
    const url = new URL(window.location.href);
    url.searchParams.set('page', nextPage);
    url.searchParams.set('section_id', this.dataset.sectionId);

    try {
      const response = await fetch(url.toString());
      const html = await response.text();
      
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Find the product list in the fetched content
      const newProductList = doc.querySelector('.product-list');
      
      if (newProductList && newProductList.children.length > 0) {
        // Append all children (products and marketing cards) to existing list
        Array.from(newProductList.children).forEach(child => {
          this.productList.appendChild(child.cloneNode(true));
        });

        this.currentPage = nextPage;
        this.highestPageLoaded = Math.max(this.highestPageLoaded, nextPage);

        // Check if there's a next page
        const newPagination = doc.querySelector('.pagination');
        const nextLink = newPagination?.querySelector('a[rel="next"]');
        this.hasMorePages = !!nextLink;

        // Update URL without page reload (only if setting is enabled)
        if (this.showPagesInUrl) {
          const newUrl = new URL(window.location.href);
          if (nextPage > 1) {
            newUrl.searchParams.set('page', nextPage);
          } else {
            newUrl.searchParams.delete('page');
          }
          window.history.replaceState({}, '', newUrl);
        }

        // Check if we need to load more immediately
        this.checkIfNeedsLoad();

      } else {
        this.hasMorePages = false;
      }

    } catch (error) {
      console.error('Error loading next page:', error);
      this.hasMorePages = false;
    } finally {
      this.isLoading = false;
      this.loadingIndicator.style.display = 'none';

      // Show end message if no more pages
      // if (!this.hasMorePages) {
      //   this.showEndMessage();
      // }
    }
  }

  showEndMessage() {
    const endMessage = document.createElement('div');
    endMessage.className = 'infinite-scroll-end';
    endMessage.style.cssText = `
      text-align: center;
      padding: 40px 20px;
      color: #999;
      font-size: 14px;
    `;
    endMessage.textContent = 'You\'ve reached the end of the collection';
    this.appendChild(endMessage);
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.boundHandleScroll);
  }
}

customElements.define('collection-infinite-scroll', CollectionInfiniteScroll);

